// frontend/src/pages/Store.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, storeApi, studentsApi } from '../api/client';
import { studentsApi as scholarDirectoryApi } from '../api/studentsApi';

export default function Store() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');

  const [statusBusy, setStatusBusy] = useState(true);
  const [statusErr, setStatusErr] = useState('');
  const [storeStatus, setStoreStatus] = useState(null);

  const [meBusy, setMeBusy] = useState(true);
  const [meErr, setMeErr] = useState('');
  const [me, setMe] = useState(null);

  const [balanceBusy, setBalanceBusy] = useState(true);
  const [balanceErr, setBalanceErr] = useState('');
  const [balance, setBalance] = useState(null);

  const [purchasesBusy, setPurchasesBusy] = useState(true);
  const [purchasesErr, setPurchasesErr] = useState('');
  const [purchases, setPurchases] = useState([]);

  // Special "incentive day" style event (null if the scholar's school has none active)
  const [event, setEvent] = useState(null);
  const [eventBalance, setEventBalance] = useState(null);
  const [eventBalanceBusy, setEventBalanceBusy] = useState(false);

  const [cart, setCart] = useState([]); // array of store items (snapshot)
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutMsg, setCheckoutMsg] = useState('');
  const [checkoutErrs, setCheckoutErrs] = useState([]);

  // Educator "shop for a scholar": search + the scholar currently selected.
  const [scholarQuery, setScholarQuery] = useState('');
  const [scholarResults, setScholarResults] = useState([]);
  const [scholarSearchBusy, setScholarSearchBusy] = useState(false);
  const [scholarSearchErr, setScholarSearchErr] = useState('');
  const [shopFor, setShopFor] = useState(null); // { id, name, house, grade, ... } | null

  const navigate = useNavigate();

  // ------------------------------------------------------------
  // Role detection based on backend truth: /api/auth/me/
  // ------------------------------------------------------------
  const isEducator = useMemo(() => {
    const role = (me?.role || me?.user?.role || '').toString().toLowerCase();
    // keep broad: educator / staff / admin / teacher all treated as "no points, browse only"
    return role === 'educator' || role === 'staff' || role === 'admin' || role === 'teacher';
  }, [me]);

  // ---------- derived values ----------
  const availablePoints = useMemo(() => {
    // Backend MyStudentBalanceView returns: { total_earned, available }
    const v =
      balance?.available ??
      balance?.available_points ??
      balance?.availablePoints ??
      balance?.balance ??
      balance?.remaining ??
      balance?.points ??
      balance?.total_points ??
      balance?.totalPoints;

    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }, [balance]);

  const pointsInCart = useMemo(() => {
    return cart.reduce((sum, it) => sum + (Number(it?.cost_points) || 0), 0);
  }, [cart]);

  const pointsRemainingIfCheckout = useMemo(() => {
    if (availablePoints === null) return null;
    return availablePoints - pointsInCart;
  }, [availablePoints, pointsInCart]);

  // ---------- event (incentive day) derived values ----------
  const regularItems = useMemo(() => items.filter((it) => !it?.event_id), [items]);
  const eventItems = useMemo(() => {
    if (!event) return [];
    return items
      .filter((it) => it?.event_id === event.id)
      // Most expensive first, alphabetical among ties.
      .sort((a, b) => (Number(b?.cost_points) || 0) - (Number(a?.cost_points) || 0) || String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [items, event]);

  const eventPointsInCart = useMemo(() => {
    if (!event) return 0;
    return cart.reduce((sum, it) => (it?.event_id === event.id ? sum + (Number(it?.cost_points) || 0) : sum), 0);
  }, [cart, event]);

  const eventItemsInCartCount = useMemo(() => {
    if (!event) return 0;
    return cart.reduce((sum, it) => (it?.event_id === event.id ? sum + 1 : sum), 0);
  }, [cart, event]);

  const eventRemainingPoints = useMemo(() => {
    const v = eventBalance?.remaining;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }, [eventBalance]);

  const eventItemsRemaining = useMemo(() => {
    const v = eventBalance?.items_remaining;
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }, [eventBalance]);

  const eventRemainingIfCheckout = useMemo(() => {
    if (eventRemainingPoints === null) return null;
    return eventRemainingPoints - eventPointsInCart;
  }, [eventRemainingPoints, eventPointsInCart]);

  const isEventOpenNow = !!event?.is_open_now;

  const isStoreOpenForScholar = useMemo(() => {
    // Scholars get a single object like { is_open_now, ... }
    // Educator/staff can see multi; that banner remains.
    if (!storeStatus) return false;
    if (storeStatus.mode === 'multi') return false; // scholars won’t be in multi mode
    return !!storeStatus.is_open_now;
  }, [storeStatus]);

  // ---------- loaders ----------
  // Every loader below optionally takes a scholarId: when an educator is
  // shopping for a scholar, every call is scoped to that scholar instead of
  // "me". Omitting it preserves the original self-service behavior.
  const loadItems = async (scholarId) => {
    setBusy(true);
    setErr('');

    const params = { active: true };
    if (scholarId) params.scholar_id = scholarId;

    const res = await storeApi.listItems(params);

    if (!res.ok) {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      setErr(res?.data?.detail || 'Unable to load store items.');
      setItems([]);
      setBusy(false);
      return;
    }

    const rows = Array.isArray(res.data?.results)
      ? res.data.results
      : (Array.isArray(res.data) ? res.data : []);
    setItems(rows);
    setBusy(false);
  };

  const loadStatus = async (scholarId) => {
    setStatusBusy(true);
    setStatusErr('');
    setStoreStatus(null);

    if (typeof storeApi.getStatus !== 'function') {
      setStatusErr('Store status endpoint not wired on the frontend yet.');
      setStatusBusy(false);
      return;
    }

    const res = await storeApi.getStatus(scholarId ? { scholar_id: scholarId } : {});

    if (!res.ok) {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      setStatusErr(res?.data?.detail || 'Unable to load store status.');
      setStatusBusy(false);
      return;
    }

    setStoreStatus(res.data);
    setStatusBusy(false);
  };

  const loadMe = async () => {
    setMeBusy(true);
    setMeErr('');
    setMe(null);

    if (!authApi || typeof authApi.me !== 'function') {
      setMeErr('User profile endpoint not wired on the frontend yet.');
      setMeBusy(false);
      return { ok: false, role: null };
    }

    const res = await authApi.me();

    if (!res.ok) {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return { ok: false, role: null };
      }
      setMeErr(res?.data?.detail || 'Unable to load your profile.');
      setMeBusy(false);
      return { ok: false, role: null };
    }

    setMe(res.data);
    setMeBusy(false);

    const role = (res.data?.role || res.data?.user?.role || '').toString().toLowerCase();
    return { ok: true, role };
  };

  const loadBalance = async (scholarId) => {
    setBalanceBusy(true);
    setBalanceErr('');
    setBalance(null);

    if (scholarId) {
      if (!studentsApi || typeof studentsApi.balanceFor !== 'function') {
        setBalanceErr('Scholar balance endpoint not wired on the frontend yet.');
        setBalanceBusy(false);
        return;
      }

      const res = await studentsApi.balanceFor(scholarId);

      if (!res.ok) {
        if (res.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        setBalanceErr(res?.data?.detail || "Unable to load this scholar's points.");
        setBalanceBusy(false);
        return;
      }

      setBalance(res.data);
      setBalanceBusy(false);
      return;
    }

    if (!studentsApi || typeof studentsApi.myBalance !== 'function') {
      setBalanceErr('Balance endpoint not wired on the frontend yet.');
      setBalanceBusy(false);
      return;
    }

    const res = await studentsApi.myBalance();

    if (!res.ok) {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      // If a student somehow doesn't have a profile, show a friendly message.
      setBalanceErr(res?.data?.detail || 'Unable to load your points.');
      setBalanceBusy(false);
      return;
    }

    setBalance(res.data);
    setBalanceBusy(false);
  };

  const loadPurchases = async (scholarId) => {
    setPurchasesBusy(true);
    setPurchasesErr('');
    setPurchases([]);

    if (!storeApi || typeof storeApi.listPurchases !== 'function') {
      setPurchasesErr('Purchases endpoint not wired on the frontend yet.');
      setPurchasesBusy(false);
      return;
    }

    const res = await storeApi.listPurchases(scholarId ? { scholar_id: scholarId } : {});

    if (!res.ok) {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      setPurchasesErr(res?.data?.detail || 'Unable to load your purchases.');
      setPurchasesBusy(false);
      return;
    }

    const rows = Array.isArray(res.data?.results)
      ? res.data.results
      : (Array.isArray(res.data) ? res.data : []);
    setPurchases(rows);
    setPurchasesBusy(false);
  };

  const loadEvent = async (scholarId) => {
    if (!storeApi || typeof storeApi.listEvents !== 'function') return null;

    const res = await storeApi.listEvents(scholarId ? { scholar_id: scholarId } : {});
    if (!res.ok) {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return null;
      }
      setEvent(null);
      return null;
    }

    // Scholar shape: { event: {...} | null }. Educator/staff shape: { events: [...] }.
    const found = res.data?.event !== undefined
      ? res.data.event
      : (Array.isArray(res.data?.events) ? res.data.events[0] : null);

    setEvent(found || null);
    return found || null;
  };

  const loadEventBalance = async (eventId, scholarId) => {
    setEventBalanceBusy(true);
    setEventBalance(null);

    if (!eventId || !storeApi || typeof storeApi.getEventBalance !== 'function') {
      setEventBalanceBusy(false);
      return;
    }

    const res = await storeApi.getEventBalance(eventId, scholarId ? { scholar_id: scholarId } : {});
    if (!res.ok) {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      setEventBalanceBusy(false);
      return;
    }

    setEventBalance(res.data);
    setEventBalanceBusy(false);
  };

  // ---------- initial load ----------
  useEffect(() => {
    let alive = true;

    (async () => {
      // Determine role first, since educators start in "browse only" (no
      // scholar selected yet) while scholars always load their own data.
      const meRes = await loadMe();
      if (!alive) return;

      const role = (meRes?.role || '').toLowerCase();
      const educator = role === 'educator' || role === 'staff' || role === 'admin' || role === 'teacher';

      await loadItems();
      if (!alive) return;
      await loadStatus();
      if (!alive) return;
      const activeEvent = await loadEvent();
      if (!alive) return;

      if (educator) {
        // Educators browse only until they pick a scholar to shop for.
        setBalanceBusy(false);
        setBalanceErr('');
        setBalance(null);

        setPurchasesBusy(false);
        setPurchasesErr('');
        setPurchases([]);
        return;
      }

      // Students: load balance + purchases (+ event budget, if there's an active event)
      await loadBalance();
      if (!alive) return;
      await loadPurchases();
      if (!alive) return;
      if (activeEvent) await loadEventBalance(activeEvent.id);
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ---------- educator: scholar search ----------
  useEffect(() => {
    if (!isEducator) return;

    const q = scholarQuery.trim();
    if (!q) {
      setScholarResults([]);
      setScholarSearchErr('');
      setScholarSearchBusy(false);
      return;
    }

    let alive = true;
    setScholarSearchBusy(true);

    const t = setTimeout(async () => {
      const res = await scholarDirectoryApi.list({ q, page_size: 10 });
      if (!alive) return;

      if (res.ok) {
        setScholarResults(res.students);
        setScholarSearchErr('');
      } else {
        setScholarResults([]);
        setScholarSearchErr(res.error || 'Unable to search scholars.');
      }
      setScholarSearchBusy(false);
    }, 300);

    return () => { alive = false; clearTimeout(t); };
  }, [scholarQuery, isEducator]);

  // ---------- educator: shop for a scholar ----------
  const selectShopFor = async (s) => {
    setShopFor(s);
    setScholarQuery('');
    setScholarResults([]);
    clearCart();

    await loadItems(s.id);
    await loadStatus(s.id);
    const ev = await loadEvent(s.id);
    await loadBalance(s.id);
    await loadPurchases(s.id);
    if (ev) {
      await loadEventBalance(ev.id, s.id);
    } else {
      setEventBalance(null);
    }
  };

  const clearShopFor = async () => {
    setShopFor(null);
    setScholarQuery('');
    setScholarResults([]);
    clearCart();

    setBalance(null);
    setBalanceBusy(false);
    setBalanceErr('');
    setPurchases([]);
    setPurchasesBusy(false);
    setPurchasesErr('');
    setEventBalance(null);

    await loadItems();
    await loadStatus();
    await loadEvent();
  };

  // ---------- banner rendering ----------
  const renderSingleBanner = (s) => {
    const isOpen = !!s.is_open_now;
    const msg = s.message || (isOpen ? 'Store is open.' : 'Store is currently closed.');
    const summary = s?.schedule?.summary;

    return (
      <div style={{ ...styles.banner, ...(isOpen ? styles.bannerOpen : styles.bannerClosed) }}>
        <div style={styles.bannerTitle}>
          {isOpen ? 'Store is OPEN' : 'Store is CLOSED'}
          {s.school_name ? <span style={styles.bannerTitleSchool}> • {s.school_name}</span> : null}
        </div>
        <div style={styles.bannerMsg}>{msg}</div>
        {summary && summary !== '(no schedule)' ? (
          <div style={styles.bannerSub}>
            Schedule: {summary}
          </div>
        ) : null}
        {s.next_open_at ? (
          <div style={styles.bannerSub}>
            Next open: {new Date(s.next_open_at).toLocaleString()}
          </div>
        ) : null}
      </div>
    );
  };

  const renderMultiBanner = (data) => {
    const schools = Array.isArray(data?.schools) ? data.schools : [];
    if (schools.length === 0) {
      return (
        <div style={{ ...styles.banner, ...styles.bannerNeutral }}>
          No school store configs found.
        </div>
      );
    }

    return (
      <div style={{ ...styles.banner, ...styles.bannerNeutral }}>
        <div style={styles.bannerTitle}>All Schools Store Status</div>
        <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
          {schools.map((s) => {
            const isOpen = !!s.is_open_now;
            const summary = s?.schedule?.summary;
            return (
              <div
                key={s.school_id}
                style={{ ...styles.schoolRow, ...(isOpen ? styles.schoolRowOpen : styles.schoolRowClosed) }}
              >
                <div style={{ fontWeight: 800 }}>
                  {s.school_name || `School ${s.school_id}`} — {isOpen ? 'OPEN' : 'CLOSED'}
                </div>
                <div style={{ color: '#333' }}>{s.message || (isOpen ? 'Store is open.' : 'Store is currently closed.')}</div>
                {summary && summary !== '(no schedule)' ? (
                  <div style={{ color: '#444', fontSize: 13 }}>Schedule: {summary}</div>
                ) : null}
                {s.next_open_at ? (
                  <div style={{ color: '#444', fontSize: 13 }}>
                    Next open: {new Date(s.next_open_at).toLocaleString()}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const banner = (() => {
    if (statusBusy) {
      return (
        <div style={{ ...styles.banner, ...styles.bannerNeutral }}>
          Checking store status…
        </div>
      );
    }
    if (statusErr) {
      return (
        <div style={{ ...styles.banner, ...styles.bannerNeutral }}>
          {statusErr}
        </div>
      );
    }
    if (!storeStatus) return null;

    // Educator/staff gets { mode: "multi", schools: [...] }
    if (storeStatus.mode === 'multi' && Array.isArray(storeStatus.schools)) {
      return renderMultiBanner(storeStatus);
    }

    // Scholar gets a single-school object
    return renderSingleBanner(storeStatus);
  })();

  // ---------- cart helpers ----------
  const inCartCountById = useMemo(() => {
    const m = new Map();
    for (const it of cart) {
      const id = it?.id;
      if (id == null) continue;
      m.set(id, (m.get(id) || 0) + 1);
    }
    return m;
  }, [cart]);

  const addToCart = (it) => {
    if (isEducator && !shopFor) return;
    setCheckoutMsg('');
    setCheckoutErrs([]);
    setCart((prev) => [...prev, it]);
  };

  const removeOneFromCart = (itemId) => {
    setCheckoutMsg('');
    setCheckoutErrs([]);

    setCart((prev) => {
      const idx = prev.findIndex((x) => x?.id === itemId);
      if (idx < 0) return prev;
      const next = prev.slice();
      next.splice(idx, 1);
      return next;
    });
  };

  const clearCart = () => {
    setCheckoutMsg('');
    setCheckoutErrs([]);
    setCart([]);
  };

  // A cart can mix regular items (gated by the school's store schedule) and
  // event items (gated by the event's own purchase window). Checkout should
  // only be blocked by whichever gate actually applies to what's in the cart.
  const cartHasRegularItems = useMemo(() => cart.some((it) => !it?.event_id), [cart]);
  const cartHasEventItems = useMemo(() => cart.some((it) => !!it?.event_id), [cart]);

  const checkoutBlockedReason = useMemo(() => {
    if (cartHasRegularItems && !isStoreOpenForScholar) return 'Store is closed';
    if (cartHasEventItems && !isEventOpenNow) return 'Event is not open for purchases';
    return null;
  }, [cartHasRegularItems, isStoreOpenForScholar, cartHasEventItems, isEventOpenNow]);

  // Compute whether you can add an item right now
  const canAddItem = (it) => {
    if (isEducator && !shopFor) return { ok: false, why: 'Select a scholar to shop for' };

    const cost = Number(it?.cost_points) || 0;
    const isEventItem = !!it?.event_id;

    if (isEventItem) {
      if (!isEventOpenNow) return { ok: false, why: 'Event is not open for purchases' };
      if (eventRemainingPoints === null) return { ok: false, why: 'Event points unavailable' };

      const eventRemaining = eventRemainingPoints - eventPointsInCart;
      if (eventRemaining < cost) return { ok: false, why: 'Not enough event-eligible points' };

      if (eventItemsRemaining !== null && eventItemsInCartCount >= eventItemsRemaining) {
        return { ok: false, why: 'You’ve reached the item limit for this event' };
      }
    } else {
      if (!isStoreOpenForScholar) return { ok: false, why: 'Store is closed' };
      if (availablePoints === null) return { ok: false, why: 'Points unavailable' };

      const remaining = availablePoints - pointsInCart;
      if (remaining < cost) return { ok: false, why: 'Not enough points' };
    }

    const stock = it?.stock_remaining ?? it?.stock ?? null;
    if (stock !== null && stock !== undefined) {
      const s = Number(stock);
      if (Number.isFinite(s) && s <= 0) return { ok: false, why: 'Out of stock' };
    }

    return { ok: true, why: '' };
  };

  const checkout = async () => {
    setCheckoutMsg('');
    setCheckoutErrs([]);

    if (isEducator && !shopFor) {
      setCheckoutErrs([{ item: '(checkout)', message: 'Select a scholar to shop for before checking out.' }]);
      return;
    }

    if (cart.length === 0) {
      setCheckoutMsg('Your cart is empty.');
      return;
    }

    if (checkoutBlockedReason) {
      setCheckoutErrs([{ item: '(checkout)', message: `${checkoutBlockedReason}. You can’t checkout right now.` }]);
      return;
    }

    setCheckoutBusy(true);

    const failures = [];
    let successCount = 0;
    const scholarId = isEducator ? shopFor?.id : undefined;

    // Purchase each cart item sequentially (simple & predictable)
    for (let i = 0; i < cart.length; i++) {
      const it = cart[i];
      const itemId = it?.id;

      if (!itemId) {
        failures.push({ item: it?.name || '(unknown item)', message: 'Missing item id.' });
        continue;
      }

      const payload = { item_id: itemId };
      if (scholarId) payload.scholar_id = scholarId;

      const res = await storeApi.purchase(payload);

      if (res.ok) {
        successCount += 1;
      } else {
        const msg =
          res?.data?.detail ||
          res?.data?.message ||
          res?.data?.error ||
          'Purchase failed.';
        failures.push({ item: it?.name || `Item ${itemId}`, message: msg });
      }
    }

    // Refresh data so points + inventory + purchase history are correct
    await loadBalance(scholarId);
    await loadItems(scholarId);
    await loadPurchases(scholarId);
    if (event) await loadEventBalance(event.id, scholarId);

    if (successCount > 0) {
      clearCart();
      setCheckoutMsg(`Purchase complete: ${successCount} item(s) purchased.`);
    }

    if (failures.length > 0) {
      setCheckoutErrs(failures);
    }

    setCheckoutBusy(false);
  };

  // ---------- UI helpers ----------
  const affordabilityChip = (it) => {
    if (isEducator && !shopFor) {
      return <span style={{ ...styles.chip, ...styles.chipNeutral }}>Browse only</span>;
    }

    const cost = Number(it?.cost_points) || 0;

    if (it?.event_id) {
      if (eventRemainingPoints === null) {
        return <span style={{ ...styles.chip, ...styles.chipNeutral }}>Event points unknown</span>;
      }
      const remaining = eventRemainingPoints - eventPointsInCart;
      const ok = remaining >= cost;
      return ok
        ? <span style={{ ...styles.chip, ...styles.chipOk }}>You can afford this</span>
        : <span style={{ ...styles.chip, ...styles.chipNo }}>Need {cost - remaining} more event points</span>;
    }

    if (availablePoints === null) {
      return <span style={{ ...styles.chip, ...styles.chipNeutral }}>Points unknown</span>;
    }

    const remaining = availablePoints - pointsInCart;
    const ok = remaining >= cost;

    return ok
      ? <span style={{ ...styles.chip, ...styles.chipOk }}>You can afford this</span>
      : <span style={{ ...styles.chip, ...styles.chipNo }}>Need {cost - remaining} more</span>;
  };

  const renderEventBanner = () => {
    if (!event) return null;

    const isOpen = isEventOpenNow;
    return (
      <div style={{ ...styles.banner, ...(isOpen ? styles.bannerOpen : styles.bannerClosed) }}>
        <div style={styles.bannerTitle}>
          {event.name}
          <span style={styles.bannerTitleSchool}> • {isOpen ? 'OPEN NOW' : 'Not open yet / closed'}</span>
        </div>
        {event.description ? <div style={styles.bannerMsg}>{event.description}</div> : null}
        <div style={styles.bannerSub}>
          Purchase window: {new Date(event.purchase_start).toLocaleString()} – {new Date(event.purchase_end).toLocaleString()}
        </div>
        <div style={styles.bannerSub}>
          Only points earned {new Date(event.points_window_start).toLocaleDateString()} – {new Date(event.points_window_end).toLocaleDateString()} count toward this event.
        </div>
      </div>
    );
  };

  const renderEventPointsPanel = () => {
    if ((isEducator && !shopFor) || !event) return null;

    if (eventBalanceBusy) {
      return <div style={{ ...styles.panel, ...styles.panelNeutral }}>Loading your event points…</div>;
    }
    if (eventRemainingPoints === null) {
      return <div style={{ ...styles.panel, ...styles.panelNeutral }}>Event points unavailable.</div>;
    }

    return (
      <div style={{ ...styles.panel, ...styles.panelNeutral }}>
        <div style={styles.panelTitle}>{event.name} Points</div>
        <div style={styles.pointsRow}>
          <div><strong>Earned in window:</strong> {eventBalance?.earned_in_window}</div>
          <div><strong>Spent on event:</strong> {eventBalance?.spent_on_event}</div>
          <div><strong>In cart:</strong> {eventPointsInCart}</div>
          <div><strong>After checkout:</strong> {eventRemainingIfCheckout}</div>
        </div>
        {eventBalance?.max_items_per_scholar !== null && eventBalance?.max_items_per_scholar !== undefined ? (
          <div style={{ ...styles.pointsRow, marginTop: 6 }}>
            <div>
              <strong>Items purchased:</strong> {eventBalance?.items_purchased ?? 0} / {eventBalance.max_items_per_scholar}
            </div>
            <div><strong>Items remaining:</strong> {Math.max(0, (eventItemsRemaining ?? 0) - eventItemsInCartCount)}</div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderShopForPanel = () => {
    if (!isEducator) return null;

    return (
      <div style={{ ...styles.panel, ...styles.panelNeutral }}>
        <div style={styles.panelTitle}>Shop for a Scholar</div>

        {shopFor ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ color: '#222' }}>
              <strong>{shopFor.name}</strong>
              {shopFor.grade !== null && shopFor.grade !== undefined && shopFor.grade !== '' ? (
                <span style={{ marginLeft: 8, color: '#444' }}>Grade {shopFor.grade}</span>
              ) : null}
              {shopFor.house ? <span style={{ marginLeft: 8, color: '#444' }}>• {shopFor.house}</span> : null}
            </div>
            <button type="button" onClick={clearShopFor} style={{ ...styles.btn, ...styles.btnGhost }}>
              Change scholar
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={scholarQuery}
              onChange={(e) => setScholarQuery(e.target.value)}
              placeholder="Search for a scholar by name…"
              style={styles.searchInput}
            />
            {scholarSearchBusy ? <div style={{ color: '#444', marginTop: 8 }}>Searching…</div> : null}
            {scholarSearchErr ? <div style={{ ...styles.error, marginTop: 8 }}>{scholarSearchErr}</div> : null}
            {!scholarSearchBusy && scholarQuery.trim() && scholarResults.length === 0 && !scholarSearchErr ? (
              <div style={{ color: '#444', marginTop: 8 }}>No scholars found.</div>
            ) : null}
            {scholarResults.length > 0 ? (
              <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                {scholarResults.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => selectShopFor(s)}
                    style={{ ...styles.btn, ...styles.btnGhost, textAlign: 'left' }}
                  >
                    {s.name}
                    {s.grade !== null && s.grade !== undefined && s.grade !== '' ? ` • Grade ${s.grade}` : ''}
                    {s.house ? ` • ${s.house}` : ''}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const renderPointsPanel = () => {
    if (meBusy) {
      return (
        <div style={{ ...styles.panel, ...styles.panelNeutral }}>
          Loading your profile…
        </div>
      );
    }

    if (meErr) {
      return (
        <div style={{ ...styles.panel, ...styles.panelNeutral }}>
          {meErr}
        </div>
      );
    }

    if (isEducator && !shopFor) {
      return (
        <div style={{ ...styles.panel, ...styles.panelNeutral }}>
          <div style={styles.panelTitle}>Points</div>
          <div style={{ color: '#222' }}>
            Search for a scholar above to see their points and shop on their behalf.
          </div>
        </div>
      );
    }

    if (balanceBusy) {
      return (
        <div style={{ ...styles.panel, ...styles.panelNeutral }}>
          Loading points…
        </div>
      );
    }

    if (balanceErr) {
      return (
        <div style={{ ...styles.panel, ...styles.panelNeutral }}>
          {balanceErr}
        </div>
      );
    }

    if (availablePoints === null) {
      return (
        <div style={{ ...styles.panel, ...styles.panelNeutral }}>
          Points unavailable.
        </div>
      );
    }

    return (
      <div style={{ ...styles.panel, ...styles.panelNeutral }}>
        <div style={styles.panelTitle}>{isEducator ? `${shopFor.name}'s Points` : 'Your Points'}</div>
        <div style={styles.pointsRow}>
          <div><strong>Available:</strong> {availablePoints}</div>
          <div><strong>In cart:</strong> {pointsInCart}</div>
          <div><strong>After checkout:</strong> {pointsRemainingIfCheckout}</div>
        </div>
      </div>
    );
  };

  const renderCartPanel = () => {
    if (isEducator && !shopFor) {
      return (
        <div style={styles.cartPanel}>
          <div style={styles.cartHeader}>
            <div style={styles.cartTitle}>Cart</div>
          </div>
          <div style={styles.cartEmpty}>
            Search for a scholar above to shop on their behalf.
          </div>
        </div>
      );
    }

    return (
      <div style={styles.cartPanel}>
        <div style={styles.cartHeader}>
          <div style={styles.cartTitle}>Cart</div>
          <button
            type="button"
            onClick={clearCart}
            disabled={checkoutBusy || cart.length === 0}
            style={{ ...styles.btn, ...styles.btnGhost, ...(checkoutBusy || cart.length === 0 ? styles.btnDisabled : null) }}
          >
            Clear
          </button>
        </div>

        {cart.length === 0 ? (
          <div style={styles.cartEmpty}>No items in cart.</div>
        ) : (
          <div style={styles.cartList}>
            {Array.from(inCartCountById.entries()).map(([id, count]) => {
              const it = cart.find((x) => x?.id === id);
              if (!it) return null;
              const cost = Number(it?.cost_points) || 0;
              return (
                <div key={id} style={styles.cartRow}>
                  <div style={{ display: 'grid' }}>
                    <div style={{ fontWeight: 800, color: '#003e73' }}>{it.name}</div>
                    <div style={{ color: '#444', fontSize: 13 }}>
                      {cost} points {count > 1 ? `× ${count}` : ''}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => removeOneFromCart(id)}
                      disabled={checkoutBusy}
                      style={{ ...styles.btn, ...styles.btnSmall, ...(checkoutBusy ? styles.btnDisabled : null) }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={styles.cartFooter}>
          <div style={{ color: '#222' }}><strong>Total:</strong> {pointsInCart} points</div>
          <button
            type="button"
            onClick={checkout}
            disabled={checkoutBusy || cart.length === 0 || !!checkoutBlockedReason}
            style={{
              ...styles.btn,
              ...styles.btnPrimary,
              ...(checkoutBusy || cart.length === 0 || checkoutBlockedReason ? styles.btnDisabled : null),
            }}
            title={checkoutBlockedReason || undefined}
          >
            {checkoutBusy ? 'Checking out…' : 'Checkout'}
          </button>
        </div>

        {checkoutMsg ? (
          <div style={{ ...styles.notice, ...styles.noticeOk }}>
            {checkoutMsg}
          </div>
        ) : null}

        {checkoutErrs.length > 0 ? (
          <div style={{ ...styles.notice, ...styles.noticeErr }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Some purchases failed:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {checkoutErrs.map((e, idx) => (
                <li key={idx}>
                  <strong>{e.item}:</strong> {e.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {checkoutBlockedReason ? (
          <div style={{ ...styles.notice, ...styles.noticeWarn }}>
            {cartHasRegularItems && !isStoreOpenForScholar
              ? 'The store is closed. You can browse items, but you can only checkout when the store is open.'
              : 'This event is not currently open for purchases.'}
          </div>
        ) : null}
      </div>
    );
  };

  const renderPurchasesPanel = () => {
    if (meBusy) return null;

    if (isEducator && !shopFor) {
      return (
        <div style={styles.panel}>
          <div style={styles.panelTitle}>Purchases</div>
          <div style={{ color: '#444' }}>
            Search for a scholar above to view their purchase history.
          </div>
        </div>
      );
    }

    return (
      <div style={styles.panel}>
        <div style={styles.panelTitle}>{isEducator ? `${shopFor.name}'s Purchases` : 'My Purchases'}</div>

        {purchasesBusy ? <div>Loading purchases…</div> : null}
        {purchasesErr ? <div style={styles.error}>{purchasesErr}</div> : null}

        {!purchasesBusy && !purchasesErr && (
          <>
            {purchases.length === 0 ? (
              <div style={{ color: '#444' }}>No purchases yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {purchases.slice(0, 50).map((p) => {
                  const name =
                    p?.item_name ??
                    p?.item?.name ??
                    p?.store_item_name ??
                    'Item';
                  const cost =
                    p?.item_cost_points ??
                    p?.cost_points ??
                    p?.item?.cost_points ??
                    '';
                  const when = p?.created_at ?? p?.purchased_at ?? p?.timestamp ?? null;

                  return (
                    <div key={p.id || `${name}-${when}`} style={styles.purchaseRow}>
                      <div style={{ fontWeight: 800, color: '#003e73' }}>
                        {name}
                        {p?.event_name ? <span style={{ fontWeight: 600 }}> • {p.event_name}</span> : null}
                      </div>
                      <div style={{ color: '#444', fontSize: 13 }}>
                        {cost !== '' ? `${cost} points` : 'points'}
                        {p?.room ? <span style={{ marginLeft: 8 }}>• {p.room}</span> : null}
                        {when ? <span style={{ marginLeft: 8 }}>• {new Date(when).toLocaleString()}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderItemCard = (it) => {
    const cost = Number(it?.cost_points) || 0;
    const add = canAddItem(it);
    const disabled = !add.ok || checkoutBusy;
    const isEventItem = !!it?.event_id;

    // Visual differentiation:
    const pointsPool = isEventItem ? eventRemainingPoints : availablePoints;
    const pointsInUse = isEventItem ? eventPointsInCart : pointsInCart;
    const canShop = !isEducator || !!shopFor;
    const shouldFade = canShop && pointsPool !== null && (pointsPool - pointsInUse) < cost;
    const closedFade = canShop && (isEventItem ? !isEventOpenNow : !isStoreOpenForScholar);
    const educatorFade = isEducator && !shopFor;
    const fade = shouldFade || closedFade || educatorFade;

    return (
      <div
        key={it.id}
        style={{
          ...styles.card,
          ...(fade ? styles.cardFaded : null),
          ...(add.ok ? styles.cardCanBuy : styles.cardCantBuy),
        }}
      >
        <div style={styles.title}>{it.name}</div>
        {it.description && <div style={styles.desc}>{it.description}</div>}

        <div style={styles.meta}>
          <strong>{it.cost_points}</strong> points
          {it.room ? <span style={{ marginLeft: 8 }}>• {it.room}</span> : null}
          {it.group_name ? <span style={{ marginLeft: 8 }}>• {it.group_name}</span> : null}
        </div>

        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {affordabilityChip(it)}

          <button
            type="button"
            onClick={() => addToCart(it)}
            disabled={disabled}
            style={{
              ...styles.btn,
              ...styles.btnPrimary,
              ...(disabled ? styles.btnDisabled : null),
            }}
            title={!add.ok ? add.why : undefined}
          >
            Add to Cart
          </button>

          {inCartCountById.get(it.id) ? (
            <span style={{ ...styles.chip, ...styles.chipNeutral }}>
              In cart: {inCartCountById.get(it.id)}
            </span>
          ) : null}
        </div>

        {!add.ok ? (
          <div style={styles.whyText}>
            {add.why}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.h1}>Store</h1>

      {renderShopForPanel()}
      {isEducator ? renderPointsPanel() : null}

      <div style={styles.twoCol}>
        <div style={{ display: 'grid', gap: 20 }}>
          {busy && <div>Loading…</div>}
          {err && <div style={styles.error}>{err}</div>}

          {/* Event section comes first, when there's an active event. The event's own
              points panel acts as the divider between it and the regular store section. */}
          {!busy && !err && event ? (
            <div style={{ display: 'grid', gap: 12 }}>
              {renderEventBanner()}
              {renderEventPointsPanel()}
              <div style={styles.grid}>
                {eventItems.length === 0 && <div>No event items yet.</div>}
                {eventItems.map((it) => renderItemCard(it))}
              </div>
            </div>
          ) : null}

          {/* Regular store section, divided from the event section above by its own
              open/closed banner + available-points panel. For educators, the points
              panel already rendered right under "Shop for a Scholar" above. */}
          {!busy && !err && (
            <div style={{ display: 'grid', gap: 12 }}>
              {banner}
              {!isEducator ? renderPointsPanel() : null}
              <div style={styles.grid}>
                {regularItems.length === 0 && <div>No active items yet.</div>}
                {regularItems.map((it) => renderItemCard(it))}
              </div>
            </div>
          )}
        </div>

        {renderCartPanel()}
      </div>

      {renderPurchasesPanel()}
    </div>
  );
}

const styles = {
  page: { display: 'grid', gap: 16 },
  h1: { margin: 0, fontSize: 20, color: '#003e73', fontWeight: 800 },

  twoCol: {
    display: 'grid',
    gap: 12,
    gridTemplateColumns: '1fr',
  },

  grid: { display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' },

  card: { background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 12 },
  cardFaded: { opacity: 0.6 },
  cardCanBuy: { boxShadow: '0 0 0 rgba(0,0,0,0)' },
  cardCantBuy: { },

  title: { fontWeight: 700, color: '#003e73' },
  desc: { marginTop: 6, color: '#333' },
  meta: { marginTop: 8, color: '#555' },

  error: { color: '#b00020', background: '#FFEAEA', border: '1px solid #F5B5B5', borderRadius: 8, padding: '8px 10px' },

  banner: {
    borderRadius: 12,
    padding: '10px 12px',
    border: '1px solid rgba(0,0,0,0.08)',
    background: '#fff',
    display: 'grid',
    gap: 4,
  },
  bannerTitle: { fontWeight: 900 },
  bannerTitleSchool: { fontWeight: 800, opacity: 0.9 },
  bannerMsg: { color: '#222' },
  bannerSub: { color: '#444', fontSize: 13 },

  bannerOpen: { background: '#E8FFF1', border: '1px solid #A7E7C1' },
  bannerClosed: { background: '#FFF3E8', border: '1px solid #F2C39B' },
  bannerNeutral: { background: '#F6F7F9', border: '1px solid rgba(0,0,0,0.08)' },

  schoolRow: {
    borderRadius: 10,
    padding: 10,
    border: '1px solid rgba(0,0,0,0.08)',
    background: '#fff',
  },
  schoolRowOpen: { background: '#E8FFF1', border: '1px solid #A7E7C1' },
  schoolRowClosed: { background: '#FFF3E8', border: '1px solid #F2C39B' },

  panel: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 12,
    padding: 12,
  },
  panelNeutral: {
    background: '#F6F7F9',
  },
  panelTitle: { fontWeight: 900, marginBottom: 8, color: '#003e73' },

  searchInput: {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid rgba(0,0,0,0.15)',
    fontSize: 14,
    boxSizing: 'border-box',
  },

  pointsRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    color: '#222',
  },

  cartPanel: {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 12,
    padding: 12,
    display: 'grid',
    gap: 10,
    alignSelf: 'start',
  },
  cartHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cartTitle: { fontWeight: 900, color: '#003e73' },
  cartEmpty: { color: '#444' },
  cartList: { display: 'grid', gap: 8 },
  cartRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 10,
    padding: 10,
    background: '#fff',
  },
  cartFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },

  purchaseRow: {
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 10,
    padding: 10,
    background: '#fff',
  },

  btn: {
    borderRadius: 10,
    padding: '8px 10px',
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#fff',
    color: '#003e73',
    cursor: 'pointer',
    fontWeight: 800,
  },
  btnSmall: {
    padding: '6px 8px',
    fontWeight: 800,
  },
  btnPrimary: {
    background: '#003e73',
    color: '#fff',
    border: '1px solid rgba(0,0,0,0.12)',
  },
  btnGhost: {
    background: '#fff',
    color: '#003e73',
    border: '1px solid rgba(0,0,0,0.12)',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  chip: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    border: '1px solid rgba(0,0,0,0.10)',
  },
  chipOk: { background: '#E8FFF1', border: '1px solid #A7E7C1', color: '#0b5a2b' },
  chipNo: { background: '#FFEAEA', border: '1px solid #F5B5B5', color: '#7a0f1a' },
  chipNeutral: { background: '#F6F7F9', color: '#222' },

  notice: {
    borderRadius: 10,
    padding: 10,
    border: '1px solid rgba(0,0,0,0.10)',
    background: '#fff',
    color: '#222',
  },
  noticeOk: { background: '#E8FFF1', border: '1px solid #A7E7C1' },
  noticeErr: { background: '#FFEAEA', border: '1px solid #F5B5B5' },
  noticeWarn: { background: '#FFF3E8', border: '1px solid #F2C39B' },

  whyText: { marginTop: 8, fontSize: 13, fontWeight: 800, color: '#7a0f1a' },
};

// Small responsive improvement without extra CSS: if the viewport is wide,
// show items + cart side-by-side.
try {
  if (typeof window !== 'undefined') {
    const w = window.innerWidth || 0;
    if (w >= 900) {
      styles.twoCol.gridTemplateColumns = '1fr 340px';
    }
  }
} catch {}
